<script setup lang="ts">
import { Chart } from "chart.js";
import { v4 as uuidv4 } from "uuid";
import type { DtwinMetricsFirmwareCountByVersion } from "~/modules/dtwin/models/DtwinMetricsFirmwareCountByVersion.type";

const props = defineProps<{
  firmwareDistribution: DtwinMetricsFirmwareCountByVersion;
  dtwinModel: string;
}>();

const { t } = useI18n();
const chartId = `chart-${uuidv4()}`;
const chartRef = ref<HTMLCanvasElement>();
const chartInstance = ref<Chart<"doughnut", number[], string> | undefined>();

const relevantFirmwareDistribution: ComputedRef<Record<string, number>> =
  computed(() => {
    const firmwareDistributionEntries = Object.entries(
      props.firmwareDistribution,
    );

    // Sort and extract the 3 most relevant firmware distributions
    const mostRelevantFirmwareDistributions = firmwareDistributionEntries
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    // Create a Set of the 3 most relevant firmware distributions keys
    const mostRelevantFirmwareDistributionsKeys = new Set(
      mostRelevantFirmwareDistributions.map(([key]) => key),
    );

    const otherDistributionSum: number = firmwareDistributionEntries.reduce(
      (accumulator: number, [key, value]: [string, number]) => {
        if (!mostRelevantFirmwareDistributionsKeys.has(key)) {
          accumulator += value;
        }
        return accumulator;
      },
      0,
    );

    const relevantFirmwareDistribution = {
      ...Object.fromEntries(mostRelevantFirmwareDistributions),
    };

    if (
      Object.prototype.hasOwnProperty.call(relevantFirmwareDistribution, "")
    ) {
      relevantFirmwareDistribution[t("dashboard.firmwares.unknown")] =
        relevantFirmwareDistribution[""];
      delete relevantFirmwareDistribution[""];
    }

    if (otherDistributionSum > 0) {
      relevantFirmwareDistribution[t("dashboard.firmwares.others")] =
        otherDistributionSum;
    }

    return relevantFirmwareDistribution;
  });

const hasFirmwareDistributionHaveEntries: ComputedRef<boolean> = computed(
  () => Object.entries(relevantFirmwareDistribution.value).length > 0,
);

const centerTextPlugin = {
  id: "centerText",
  beforeDraw(chart: Chart) {
    const { height } = chart;
    const ctx = chart.ctx;
    ctx.restore();

    const fontSize = 16;
    const fontFamily = "Montserrat";
    const fontWeight = 600;
    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillStyle = "#002F5D";

    const text = props.dtwinModel;

    const meta = chart.getDatasetMeta(0);
    const firstArc = meta.data[0];

    const textX = firstArc.x;
    const textY = height / 2;

    ctx.fillText(text, textX, textY);
    ctx.save();
  },
};

const dataChart = computed(() => {
  return {
    labels: Object.keys(relevantFirmwareDistribution.value),
    datasets: [
      {
        label: "",
        data: Object.values(relevantFirmwareDistribution.value),
        backgroundColor: ["#F79009", "#1B538D", "#002F5D", "#98A2B3"],
      },
    ],
  };
});

const buildChart = () => {
  const chartCtx = chartRef.value?.getContext("2d");

  if (chartCtx) {
    chartInstance.value = new Chart(chartCtx, {
      type: "doughnut",
      data: dataChart.value,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        layout: {
          padding: {
            right: 16,
          },
        },
        plugins: {
          legend: {
            position: "right",
            labels: {
              pointStyle: "circle",
              usePointStyle: true,
              boxWidth: 8,
              boxHeight: 8,
              padding: 14,
              font: {
                size: 14,
                style: "normal",
                weight: "400",
              },
              color: "#475467",
            },
          },
        },
      },
      plugins: [centerTextPlugin],
    });
  }
};

watch(
  () => props.firmwareDistribution,
  () => buildChart(),
);

onMounted(buildChart);
</script>

<template>
  <div v-if="hasFirmwareDistributionHaveEntries" class="flex justify-center">
    <canvas :id="chartId" ref="chartRef" height="160" />
  </div>
</template>
